import React, { useEffect, useRef, useState } from 'react';
import { Select, SelectMultiple } from 'grommet';
import PropTypes from 'prop-types';
import { API } from '../api';
import QueryString from 'qs';
import { Utils } from '../helpers/Utils';
import { buildLocationFilters } from '../helpers/LocationSearch';
import { useDebouncedCallback } from 'use-debounce';

const NAME_KEYS = {
    companies: 'name',
    industries: 'name',
    jobs: 'title',
    'job-functions': 'name',
    majors: 'name',
    'technical-skills': 'name',
};

const PLACEHOLDERS = {
    companies: 'Select companies',
    industries: 'Select industries',
    jobs: 'Select jobs',
    'job-functions': 'Select job functions',
    locations: 'Select locations',
    majors: 'Select majors',
    'technical-skills': 'Select technical skills',
};

// Labels come straight from the API, so a nullable name (or one trimmed
// away by a caller's `fields`) yields an undefined label. Guard the sort so
// one such row cannot throw mid-render and blank the whole form.
const byLabel = (a, b) => (a.label ?? '').localeCompare(b.label ?? '');

const toLabel = (contentType, nameKey, r) => {
    if (contentType === 'locations') return `${r[nameKey]}, ${r.state ?? '?'}`;
    if (contentType === 'jobs') return `${r[nameKey]} [${r.company.name}]`;
    return r[nameKey];
};

// `dropHeight` caps the drop so its option list is its own scroll container.
// Without it the list grows to full height, Grommet's InfiniteScroll falls
// back to the page as its scroll parent, and a scrolled-down form makes it
// skip the first page of options and render a tall blank spacer instead.
export const ApiSearch = ({
    contentType,
    selectMultiple = false,
    clearable = false,
    defaultValue,
    dropHeight = 'medium',
    filters,
    placeholder,
    populate,
    updateFormData,
}) => {
    const nameKey = NAME_KEYS[contentType] ?? 'name';

    const toOption = (r) => ({
        label: toLabel(contentType, nameKey, r),
        value: r,
        id: r.id,
    });

    // Seed both lists from the current selection. Grommet resolves each
    // selected chip's label by looking it up inside `options`, so an empty
    // `options` renders the saved values as nothing at all.
    const initialOptions = (defaultValue ?? []).map(toOption).sort(byLabel);

    const [options, setOptions] = useState(initialOptions);
    const [selectedOptions, setSelectedOptions] = useState(initialOptions);

    // Only the newest request may write to `options`. Without this a broad,
    // slow response can land after a narrower, faster one and leave the list
    // showing results for a search the user already changed.
    const latestRequest = useRef(0);

    const fetchOptions = async (input = '') => {
        const requestId = ++latestRequest.current;
        const results = await searchContentType(
            contentType,
            nameKey,
            input,
            populate,
            filters,
        );
        if (requestId !== latestRequest.current) return;
        // Selected items stay pinned on top and are never dropped, so an
        // empty or failed search cannot blank the chips or the selection.
        const pinned = [...selectedOptions].sort(byLabel);
        const selectedIds = pinned.map((o) => o.value.id);
        const fresh = results
            .filter((r) => !selectedIds.includes(r.id))
            .map(toOption)
            .sort(byLabel);
        setOptions([...pinned, ...fresh]);
    };

    const debouncedFetch = useDebouncedCallback((input) => {
        fetchOptions(input);
    }, 300);

    const handleSearch = (input) => {
        if (!input.length) {
            // Cancel the armed debounce, or clearing the box would still be
            // overtaken by the keystrokes the user just deleted.
            debouncedFetch.cancel();
            fetchOptions('');
        } else {
            debouncedFetch(input);
        }
    };

    const handleOpen = () => fetchOptions('');

    // Closing needs no request: the next open refetches the unfiltered list
    // anyway, so fetching here only doubles the traffic per open/close.
    const handleClose = () => debouncedFetch.cancel();

    const handleChangeMultiple = ({ value }) => {
        setSelectedOptions(value);
        updateFormData(value.map((o) => o.value));
    };

    // Grommet's `clear` control fires onChange with no `option`, which is the
    // only way a single Select can report "nothing selected".
    const handleChangeSingle = ({ option }) => {
        if (!option) {
            setSelectedOptions([]);
            updateFormData([]);
            return;
        }
        setSelectedOptions([option]);
        updateFormData([option.value]);
    };

    useEffect(() => {
        if (defaultValue && defaultValue.length > 0) return;
        updateFormData([]);
    }, []);

    // `initialOptions` only seeds the first render, so a parent that swaps
    // `defaultValue` without remounting (a `useRevalidator()` re-run after a
    // save) would keep the stale chips. Re-seed both lists when the selected
    // ids change, keeping any already-fetched options below them.
    const defaultIds = (defaultValue ?? []).map((r) => r.id).join(',');

    useEffect(() => {
        const pinned = (defaultValue ?? []).map(toOption).sort(byLabel);
        const pinnedIds = pinned.map((o) => o.id);
        setSelectedOptions(pinned);
        setOptions((prev) => [
            ...pinned,
            ...prev.filter((o) => !pinnedIds.includes(o.id)),
        ]);
    }, [defaultIds]);

    const resolvedPlaceholder =
        placeholder || PLACEHOLDERS[contentType] || 'Select';

    if (selectMultiple) {
        return (
            <SelectMultiple
                placeholder={resolvedPlaceholder}
                searchPlaceholder="Search..."
                dropHeight={dropHeight}
                options={options}
                labelKey="label"
                valueKey="id"
                value={selectedOptions}
                onChange={handleChangeMultiple}
                onOpen={handleOpen}
                onClose={handleClose}
                onSearch={handleSearch}
                showSelectedInline={true}
            />
        );
    }

    return (
        <Select
            placeholder={resolvedPlaceholder}
            searchPlaceholder="Search..."
            clear={clearable ? { label: 'Clear selection' } : undefined}
            dropHeight={dropHeight}
            options={options}
            labelKey="label"
            valueKey="label"
            value={selectedOptions[0] ?? null}
            onChange={handleChangeSingle}
            onOpen={handleOpen}
            onClose={handleClose}
            onSearch={handleSearch}
        />
    );
};

async function searchContentType(
    contentType,
    nameKey,
    input,
    populate,
    filters = {},
) {
    const searchFilters =
        contentType === 'locations'
            ? { ...filters, ...buildLocationFilters(input) }
            : { ...filters, [nameKey]: { $containsi: input } };

    const sort =
        contentType === 'locations'
            ? ['name:asc', 'state:asc']
            : [`${nameKey}:asc`];

    const query = QueryString.stringify({
        filters: searchFilters,
        pagination: {
            limit: 500,
        },
        populate: populate,
        sort,
    });

    try {
        const response = await API.get(`/api/${contentType}?${query}`);
        return Utils.deepFlatten(response.data);
    } catch (error) {
        console.log(error);
        return [];
    }
}

ApiSearch.propTypes = {
    clearable: PropTypes.bool,
    contentType: PropTypes.string,
    defaultValue: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    dropHeight: PropTypes.string,
    filters: PropTypes.object,
    placeholder: PropTypes.string,
    populate: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    selectMultiple: PropTypes.bool,
    updateFormData: PropTypes.func,
};
